import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import { useCountriesQuery } from '@modules/countries/queries';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';

import type { IEditTrackerCountryForm, ITrackerChangeCountryOptions } from '../types';
import { AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS, TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS } from '../constants';
import { useAffiliateTrackerCountryQuery } from '../queries';

export interface ITrackerChangeCountryComposable {
  trackerCountryFormSchema: ComputedRef<FormSchema<IEditTrackerCountryForm>>;
  isLoadingCountries: Ref<boolean>;
  isLoadingTrackerCountry: Ref<boolean>;
  showSavingCountryWarning: Ref<boolean>;
  updateCountryPermission: Ref<boolean>;
  isReadonlyForm: Ref<boolean>;
  onChange(key: keyof IEditTrackerCountryForm, value: IEditTrackerCountryForm[keyof IEditTrackerCountryForm]): void;
}

export default function useTrackerChangeCountry(options: Ref<ITrackerChangeCountryOptions>): ITrackerChangeCountryComposable {
  const { getTranslation } = useI18n();
  const { hasPermission } = usePermissionsStore();

  const showSavingCountryWarning = ref(false);

  const updateCountryPermission = computed<boolean>(() => hasPermission(AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.UPDATE_COUNTRY).value);

  const isQueryEnabled = computed(() => options.value.isQueryEnabled && updateCountryPermission.value);
  const affId = computed(() => options.value.affId);
  const trackerId = computed(() => options.value.trackerId);

  const { data: countries, isPending: isLoadingCountries } = useCountriesQuery(isQueryEnabled);

  const { data: trackerCountry, isPending: isLoadingTrackerCountry } = useAffiliateTrackerCountryQuery(affId, trackerId, isQueryEnabled);

  const countriesOptions = computed<IFormSelectInputOption<number>[]>(() => {
    if (!countries.value) {
      return [];
    }

    return countries.value.map((item) => ({
      value: item.country_code,
      label: item.name,
    }));
  });

  const descriptionText = computed<string | undefined>(() => {
    return showSavingCountryWarning.value ? getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SAVE_TRACKER_COUNTRY_WARNING').value : undefined;
  });

  const isReadonlyForm = computed<boolean>(() => !!trackerCountry.value?.country_code);

  const trackerCountryFormSchema = computed<FormSchema<IEditTrackerCountryForm>>(() => ({
    country_code: {
      type: EFormInputTypes.SELECT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'EDIT_TRACKER_COUNTRY_LABEL').value,
      value: trackerCountry.value?.country_code === 0 ? undefined : trackerCountry.value?.country_code,
      options: countriesOptions.value,
      required: true,
      hideErrors: isReadonlyForm.value,
      loading: isLoadingCountries.value,
      description: descriptionText.value,
      descriptionSeverity: 'warn',
    },
    info: {
      type: EFormInputTypes.SLOT,
    },
  }));

  function onChange(key: keyof IEditTrackerCountryForm, value: IEditTrackerCountryForm[keyof IEditTrackerCountryForm]): void {
    if (key === 'country_code' && !!value) {
      showSavingCountryWarning.value = true;
    }
  }

  return {
    trackerCountryFormSchema,
    isLoadingCountries,
    isLoadingTrackerCountry,
    showSavingCountryWarning,
    updateCountryPermission,
    isReadonlyForm,
    onChange,
  };
}
